/**
 * Каркас мини-аппа: шапка с балансом и нижняя навигация.
 *
 * Навигация нижняя и в пределах большого пальца: мини-апп держат одной рукой.
 * Отступы считаются от safe-area, иначе на iPhone кнопки уезжают под
 * системную полосу жестов.
 *
 * Мини-чат живёт отдельным слоем: плавающая кнопка поверх любого режима,
 * чтобы написать в краше, не уходя со стола.
 */

import type { ReactNode } from "react";

import { formatPaws } from "@koshkodrop/shared";

import { ChatDock } from "../chat/ChatDock";
import { haptic } from "../lib/telegram";
import { type Screen, type Tab } from "../navigation";
import { useSession } from "../store/session";

export type { Tab };

const TABS: { id: Tab; label: string; icon: string }[] = [
  { id: "home", label: "Игры", icon: "🎰" },
  { id: "inventory", label: "Инвентарь", icon: "🎒" },
  { id: "wallet", label: "Кошелёк", icon: "🐾" },
  { id: "profile", label: "Профиль", icon: "😺" },
];

interface Props {
  tab: Tab;
  onTabChange: (tab: Screen) => void;
  children: ReactNode;
}

export function Layout({ tab, onTabChange, children }: Props) {
  const { user, balance } = useSession();

  return (
    <div className="flex min-h-full flex-col bg-night-950">
      <header className="safe-top sticky top-0 z-20 border-b border-white/5 bg-night-950/85 backdrop-blur-xl">
        <div className="flex items-center justify-between gap-3 px-4 pb-3">
          <div className="flex items-center gap-2.5">
            <span className="text-2xl">🐈‍⬛</span>
            <div className="leading-tight">
              <p className="text-sm font-extrabold text-white">Кошкодроп</p>
              <p className="tabular text-[11px] text-white/40">
                {user ? `ID ${user.telegramId}` : "гость"}
              </p>
            </div>
          </div>

          <div className="glow-paws flex items-center gap-1.5 rounded-full bg-night-850 px-3.5 py-2">
            <span className="text-sm">🐾</span>
            <span className="tabular text-sm font-extrabold text-paws-300">
              {formatPaws(balance, { decimals: 2 })}
            </span>
          </div>
        </div>
      </header>

      <main className="flex-1 px-4 pb-28 pt-4">{children}</main>

      <nav className="safe-bottom fixed inset-x-0 bottom-0 z-20 border-t border-white/5 bg-night-900/95 backdrop-blur-xl">
        <div className="flex items-stretch">
          {TABS.map((item) => {
            const active = item.id === tab;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => {
                  haptic.select();
                  onTabChange(item.id);
                }}
                className={`flex flex-1 flex-col items-center gap-0.5 py-2.5 transition-colors ${
                  active ? "text-paws-400" : "text-white/40"
                }`}
              >
                <span className={`text-xl ${active ? "animate-pop" : ""}`}>{item.icon}</span>
                <span className="text-[10px] font-bold">{item.label}</span>
              </button>
            );
          })}
        </div>
      </nav>

      <ChatDock />
    </div>
  );
}
